import type { User } from '@/stores/auth.js';
import type { ShallowRef } from 'vue';
import { useSessionStore } from '@/stores/session.ts';
import { SessionInteractionAction, type SessionInteractionPayload } from '@pokerly/contracts/events/common';
import { emojiIndex } from '@/lib/emoji.ts';
import type { EmojiData } from 'emoji-mart-vue-fast/src';
import type { Participant } from '@/types/participant.js';
import { useProjectile } from '@/components/projectile/use-projectile.ts';
import { useParticipantsStore } from '@/stores/participants.ts';

export interface AvatarComponent {
    $props: {
        participant: Participant | User;
    };
    getElement?: () => HTMLElement | null;
}

export interface UseEmojiThrowHandlerOptions {
    /** Reference to the array of avatar components (may contain nulls from template refs) */
    avatarsRef: ShallowRef<(AvatarComponent | null)[] | null>;
}

/**
 * Composable for handling emoji throw interactions on the session board.
 *
 * Manages both:
 * - Receiving emoji throws from other users (via WebSocket events)
 * - Sending emoji throws to other users
 *
 * @example
 * ```ts
 * const avatarsRef = useTemplateRef('avatars');
 * const { handleEmojiThrow, setupInteractionListener } = useEmojiThrowHandler({
 *   avatarsRef,
 * });
 *
 * // Set up listener on mount
 * setupInteractionListener();
 * ```
 */
export function useEmojiThrowHandler(options: UseEmojiThrowHandlerOptions) {
    const { avatarsRef } = options;
    const { spawn } = useProjectile();
    const sessionStore = useSessionStore();
    const participantsStore = useParticipantsStore();

    /**
     * Find an avatar component by participant ID.
     */
    function findAvatarComponentByParticipantId(participantId: string): AvatarComponent | undefined {
        return avatarsRef.value
            ?.filter((component) => !!component)
            .find((component) => component.$props.participant.id === participantId);
    }

    /**
     * Get the DOM element for a participant's avatar.
     */
    function getAvatarElement(participantId: string): HTMLElement | null {
        const component = findAvatarComponentByParticipantId(participantId);
        if (!component) return null;

        if (typeof component.getElement === 'function') {
            return component.getElement();
        }

        return null;
    }

    /**
     * Spawn a projectile emoji animation between two points.
     */
    function spawnEmojiProjectile(emojiText: string, origin: DOMRect, target: DOMRect): void {
        spawn({
            id: `emoji-${Date.now()}${Math.random()}`,
            text: emojiText,
            from: {
                x: origin.left - 8 + origin.width / 2,
                y: origin.top - 8 + origin.height / 2,
            },
            toRect: target,
            class: 'z-10',
        });
    }

    function getObserverRect(participantId?: string): DOMRect | null {
        if (!participantId || !participantsStore.observers.some((p) => p.id === participantId)) return null;
        // Simulate a DOM Element located of the left border of the scree
        const minY = window.innerHeight * 0.25;
        const maxY = window.innerHeight * 0.6;
        const randomY = Math.random() * (maxY - minY) + minY;
        return new DOMRect(16, randomY - 32, 64, 64);
    }

    /**
     * Handle receiving an emoji throw interaction from another user.
     */
    function handleIncomingInteraction(event: SessionInteractionPayload): void {
        if (!avatarsRef.value || !avatarsRef.value.length) return;
        if (event.action !== SessionInteractionAction.EmojiThrow) return;

        const { emoji: emojiId, fromUserId, toUserId } = event.payload;
        const emoji = emojiIndex.tryEmoji(emojiId);
        if (!emoji) return;

        const fromEl = getAvatarElement(fromUserId)?.getBoundingClientRect() ?? getObserverRect(fromUserId);
        const toEl = getAvatarElement(toUserId)?.getBoundingClientRect();
        if (!fromEl || !toEl) return;

        spawnEmojiProjectile(emoji.native, fromEl, toEl);
    }

    /**
     * Handle throwing an emoji to another participant.
     * Spawns the animation locally and sends the interaction to the server.
     */
    function handleEmojiThrow(emoji: EmojiData, target: DOMRect, targetParticipantId: string): void {
        // Find the current user's avatar
        const thisUser =
            avatarsRef.value
                ?.filter((component) => !!component)
                .find((component) => sessionStore.isAuthenticatedUser(component.$props.participant)) ??
            getObserverRect(participantsStore.thisUser?.id);

        if (!thisUser) return;

        let origin: DOMRect | null;
        // if thisUser is an HTMLElement (observer case)
        if (thisUser instanceof DOMRect) origin = thisUser;
        else
            origin =
                typeof thisUser.getElement === 'function'
                    ? (thisUser.getElement()?.getBoundingClientRect() ?? null)
                    : null;
        if (!origin) return;

        // Send interaction to server
        sessionStore.sendEmojiThrowInteraction(targetParticipantId, emoji.id);

        // Spawn local animation
        spawn({
            id: `emoji-${Date.now()}${Math.random()}`,
            text: emoji.native,
            from: {
                x: origin.left - 8 + origin.width / 2,
                y: origin.top - 8 + origin.height / 2,
            },
            toRect: target,
            class: 'z-10',
        });
    }

    /**
     * Set up the listener for incoming emoji throw interactions.
     * Call this in onMounted or during setup.
     */
    function setupInteractionListener(): void {
        sessionStore.onInteractionReceived(handleIncomingInteraction);
    }

    return {
        // Methods
        handleEmojiThrow,
        handleIncomingInteraction,
        setupInteractionListener,
        findAvatarComponentByParticipantId,
        getAvatarElement,
        spawnEmojiProjectile,
    };
}
